import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Loader2, ShieldCheck, PauseCircle, Eye, EyeOff, Copy, Trash2, KeyRound, AlertTriangle, CheckSquare, Square, Gauge, Save } from "lucide-react";

function GlobalTaskLimit({ current, onChange }) {
  const [val, setVal] = useState(current);
  React.useEffect(() => { setVal(current); }, [current]);
  const save = useMutation({
    mutationFn: (v) => api.patch("/settings/task-limits", { default_task_limit: parseInt(v, 10) }),
    onSuccess: (r) => { toast.success(`Global default set to ${r.data.default_task_limit}/mo`); onChange?.(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  return (
    <div className="border border-white/10 bg-[var(--surface)] p-5 mb-6" data-testid="global-task-limit-card">
      <div className="flex items-center gap-3 mb-3">
        <Gauge className="w-4 h-4 text-[#FF3B30]"/>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">// per-month task cap</div>
          <div className="font-display font-black text-xl tracking-tight">Monthly task limit.</div>
        </div>
      </div>
      <div className="text-[12px] text-zinc-400 mb-3 leading-relaxed">
        Applied to every agent unless they have an individual override below. Agents at their cap are auto-excluded from new campaigns until next month or a limit raise.
      </div>
      <div className="flex items-center gap-3">
        <input
          type="number" min={0} max={1000}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          data-testid="global-task-limit-input"
          className="w-24 bg-[#050505] border border-white/10 px-3 py-2 text-sm font-mono"
        />
        <span className="text-[12px] font-mono text-zinc-500">tasks / agent / month</span>
        <button
          onClick={() => save.mutate(val)}
          disabled={save.isPending || val === "" || parseInt(val, 10) === current}
          data-testid="save-global-task-limit-btn"
          className="ml-auto bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-medium px-4 py-2 text-[13px] flex items-center gap-2 disabled:opacity-40"
        >
          {save.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
          SET DEFAULT
        </button>
      </div>
    </div>
  );
}

function TaskLimitCell({ agentId, limitRow, defaultLimit, onChanged }) {
  const [val, setVal] = useState(limitRow?.task_limit ?? "");
  const [editing, setEditing] = useState(false);
  const save = useMutation({
    mutationFn: (payload) => api.patch(`/users/${agentId}/task-limit`, payload),
    onSuccess: () => { toast.success("Limit updated"); setEditing(false); onChanged?.(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const effective = limitRow?.effective_limit ?? defaultLimit;
  const usage = limitRow?.usage ?? 0;
  const pct = effective > 0 ? Math.min(100, Math.round((usage / effective) * 100)) : 0;
  const capped = usage >= effective;

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          type="number" min={0} max={1000}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder={`default ${defaultLimit}`}
          data-testid={`task-limit-input-${agentId}`}
          className="w-20 bg-[#050505] border border-white/10 px-1.5 py-1 text-[11px] font-mono"
        />
        <button
          onClick={() => save.mutate({ task_limit: val === "" ? null : parseInt(val, 10) })}
          disabled={save.isPending}
          data-testid={`save-task-limit-${agentId}`}
          className="p-1 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
          title="Save"
        >
          <Save className="w-3 h-3"/>
        </button>
        <button
          onClick={() => { setEditing(false); setVal(limitRow?.task_limit ?? ""); }}
          className="p-1 border border-white/10 text-zinc-400 hover:text-white"
          title="Cancel"
        >
          <Trash2 className="w-3 h-3"/>
        </button>
      </div>
    );
  }
  return (
    <button
      onClick={() => setEditing(true)}
      data-testid={`task-limit-cell-${agentId}`}
      className="flex flex-col items-start w-full min-w-[110px] group"
    >
      <div className={`font-mono text-[11px] ${capped ? "text-[#FF3B30]" : "text-zinc-300"} group-hover:text-white flex items-center gap-1.5`}>
        <span>{usage}/{effective}</span>
        {limitRow?.task_limit != null && <span className="text-[9px] text-amber-400">custom</span>}
        {capped && <span className="text-[9px] uppercase tracking-widest">capped</span>}
      </div>
      <div className="w-full h-1 bg-[#1a1a1c] mt-1 relative overflow-hidden">
        <div className={`h-full ${capped ? "bg-[#FF3B30]" : "bg-emerald-400"}`} style={{ width: `${pct}%` }}/>
      </div>
    </button>
  );
}

function PasswordCell({ userId }) {
  const [revealed, setRevealed] = useState(false);
  const [password, setPassword] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notAvailable, setNotAvailable] = useState(false);

  const reveal = async () => {
    if (password) { setRevealed(true); return; }
    setLoading(true);
    try {
      const r = await api.get(`/users/${userId}/password`);
      setPassword(r.data.password);
      setRevealed(true);
    } catch (e) {
      if (e?.response?.status === 410) {
        setNotAvailable(true);
        toast.info("No plaintext password on file — use Reset to generate one.");
      } else {
        toast.error(e?.response?.data?.detail || "Failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(password);
    toast.success("Password copied");
  };

  if (notAvailable) {
    return <span className="font-mono text-[11px] text-zinc-600">— reset required —</span>;
  }
  if (!revealed) {
    return (
      <button
        onClick={reveal}
        disabled={loading}
        data-testid={`reveal-password-${userId}`}
        className="font-mono text-[11px] text-zinc-500 hover:text-white flex items-center gap-1"
      >
        {loading ? <Loader2 className="w-3 h-3 animate-spin"/> : <Eye className="w-3 h-3"/>}
        {loading ? "…" : "reveal"}
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <code className="font-mono text-[12px] text-emerald-300 bg-[#050505] px-1.5 py-0.5">{password}</code>
      <button onClick={copy} data-testid={`copy-password-${userId}`} className="text-zinc-500 hover:text-white">
        <Copy className="w-3 h-3"/>
      </button>
      <button onClick={() => setRevealed(false)} className="text-zinc-500 hover:text-white">
        <EyeOff className="w-3 h-3"/>
      </button>
    </div>
  );
}

export default function Agents() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [confirmDelete, setConfirmDelete] = useState(null); // user or null
  const [selected, setSelected] = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);

  const users = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get("/users").then(r => r.data),
  });

  const limits = useQuery({
    queryKey: ["task-limits"],
    queryFn: () => api.get("/settings/task-limits").then(r => r.data),
  });
  const limitByAgent = React.useMemo(() => {
    const m = {};
    (limits.data?.agents || []).forEach(a => { m[a.id] = a; });
    return m;
  }, [limits.data]);
  const defaultLimit = limits.data?.default_task_limit ?? 30;
  const refreshLimits = () => qc.invalidateQueries({ queryKey: ["task-limits"] });

  const create = useMutation({
    mutationFn: (body) => api.post("/users", body),
    onSuccess: () => { toast.success("Agent created"); setForm({ name:"", email:"", password:"" }); qc.invalidateQueries({queryKey:["users"]}); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to create"),
  });

  const toggle = useMutation({
    mutationFn: ({id, status}) => api.patch(`/users/${id}/status?status=${status}`),
    onSuccess: () => { qc.invalidateQueries({queryKey:["users"]}); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const del = useMutation({
    mutationFn: ({ id, force }) => api.delete(`/users/${id}${force ? "?force=true" : ""}`),
    onSuccess: (r) => {
      const c = r.data.cancelled_tasks;
      toast.success(c ? `Agent deleted (${c} tasks cancelled)` : "Agent deleted");
      setConfirmDelete(null);
      qc.invalidateQueries({queryKey:["users"]});
    },
    onError: (e) => {
      if (e?.response?.status === 409) {
        // Offer force-delete
        const msg = e.response.data.detail;
        if (window.confirm(`${msg}\n\nForce delete and cancel their tasks?`)) {
          del.mutate({ id: confirmDelete?.id, force: true });
        }
      } else {
        toast.error(e?.response?.data?.detail || "Delete failed");
      }
    },
  });

  const bulkDel = useMutation({
    mutationFn: ({ ids, force }) => api.post("/users/bulk-delete", { ids, force }),
    onSuccess: (r) => {
      const d = r.data.deleted?.length || 0;
      const s = r.data.skipped?.length || 0;
      const c = r.data.cancelled_tasks || 0;
      toast.success(`${d} deleted${s ? ` · ${s} skipped` : ""}${c ? ` · ${c} tasks cancelled` : ""}`);
      setSelected(new Set());
      setBulkConfirm(false);
      qc.invalidateQueries({queryKey:["users"]});
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Bulk delete failed"),
  });

  const resetPw = useMutation({
    mutationFn: (id) => api.post(`/users/${id}/reset-password`),
    onSuccess: (r) => {
      navigator.clipboard.writeText(r.data.password);
      toast.success(`New password copied: ${r.data.password}`);
      qc.invalidateQueries({queryKey:["users"]});
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Reset failed"),
  });

  return (
    <Layout title="Agent Roster." subtitle="Distributed operator pool">
      <GlobalTaskLimit current={defaultLimit} onChange={refreshLimits}/>
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setConfirmDelete(null)}>
          <div className="w-full max-w-md bg-[#09090B] border border-[#FF3B30]/40 p-6" onClick={(e)=>e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-[#FF3B30] shrink-0"/>
              <div>
                <div className="font-display font-black text-xl tracking-tight">Delete {confirmDelete.name}?</div>
                <div className="text-[13px] text-zinc-400 mt-1">This soft-deletes the account. If they have active tasks, you'll be asked to force-delete (which cancels those tasks).</div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 border border-white/15 hover:border-white/30 py-2 text-[13px]">Cancel</button>
              <button
                onClick={() => del.mutate({ id: confirmDelete.id, force: false })}
                disabled={del.isPending}
                data-testid="confirm-delete-user-btn"
                className="flex-1 bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-medium py-2 text-[13px] flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {del.isPending && <Loader2 className="w-4 h-4 animate-spin"/>}
                DELETE
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setBulkConfirm(false)}>
          <div className="w-full max-w-md bg-[#09090B] border border-[#FF3B30]/40 p-6" onClick={(e)=>e.stopPropagation()} data-testid="bulk-confirm-modal">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-[#FF3B30] shrink-0"/>
              <div>
                <div className="font-display font-black text-xl tracking-tight">Delete {selected.size} agents?</div>
                <div className="text-[13px] text-zinc-400 mt-1">Any active tasks for these agents will be cancelled. Cannot be undone.</div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setBulkConfirm(false)} className="flex-1 border border-white/15 hover:border-white/30 py-2 text-[13px]">Cancel</button>
              <button
                onClick={() => bulkDel.mutate({ ids: [...selected], force: true })}
                disabled={bulkDel.isPending}
                data-testid="bulk-confirm-btn"
                className="flex-1 bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-medium py-2 text-[13px] flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {bulkDel.isPending && <Loader2 className="w-4 h-4 animate-spin"/>}
                FORCE DELETE {selected.size}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-white/10">
          {selected.size > 0 && (
            <div className="px-5 py-3 border-b border-white/10 bg-[#FF3B30]/10 flex items-center gap-3" data-testid="bulk-toolbar">
              <span className="font-mono text-[11px] text-white uppercase tracking-widest">{selected.size} selected</span>
              <button onClick={() => setSelected(new Set())} className="text-[11px] font-mono text-zinc-400 hover:text-white">clear</button>
              <button
                onClick={() => setBulkConfirm(true)}
                data-testid="bulk-delete-btn"
                className="ml-auto border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white px-3 py-1 text-[11px] font-mono uppercase tracking-widest flex items-center gap-1"
              >
                <Trash2 className="w-3 h-3"/> Delete selected
              </button>
            </div>
          )}
          {/* Desktop table header — hidden on mobile */}
          <div className="hidden md:grid grid-cols-12 px-5 py-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500 border-b border-white/10">
            <div className="col-span-1"></div>
            <div className="col-span-3">Name</div>
            <div className="col-span-2">Email</div>
            <div className="col-span-2">Password</div>
            <div className="col-span-2">Task cap (mo)</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {users.isLoading && <div className="p-6 text-zinc-500 text-sm">Loading…</div>}
          {users.data?.map((usr) => (
            <div key={usr.id} data-testid={`user-row-${usr.id}`}
                 className="flex flex-col md:grid md:grid-cols-12 md:items-center px-4 md:px-5 py-4 border-b border-white/5 hover:bg-[#0e0e10] gap-3 md:gap-2">
              {/* Mobile-friendly row 1: checkbox + name + role badge */}
              <div className="flex items-start justify-between md:col-span-1 md:justify-center">
                {usr.role === "agent" ? (
                  <button
                    onClick={() => {
                      const n = new Set(selected);
                      n.has(usr.id) ? n.delete(usr.id) : n.add(usr.id);
                      setSelected(n);
                    }}
                    data-testid={`select-user-${usr.id}`}
                    className="text-zinc-400 hover:text-white"
                  >
                    {selected.has(usr.id) ? <CheckSquare className="w-4 h-4 text-[var(--accent)]"/> : <Square className="w-4 h-4"/>}
                  </button>
                ) : <span className="w-4"/>}
              </div>
              <div className="md:col-span-3 min-w-0">
                <div className="text-[14px] text-zinc-100 truncate">
                  {usr.role === "agent" ? (
                    <a href={`/admin/agents/${usr.id}`} data-testid={`open-agent-${usr.id}`} className="hover:text-[var(--accent)] hover:underline">{usr.name}</a>
                  ) : usr.name}
                </div>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {usr.reddit_username && (
                    <span data-testid={`agent-reddit-${usr.id}`} className="font-mono text-[10px] text-[var(--accent)]">
                      u/{String(usr.reddit_username).replace(/^u\//,"")}
                    </span>
                  )}
                  {usr.telegram_username ? (
                    <span data-testid={`agent-telegram-${usr.id}`} className="font-mono text-[10px] text-emerald-400">
                      @{usr.telegram_username}
                    </span>
                  ) : usr.role === "agent" ? (
                    <span data-testid={`agent-telegram-missing-${usr.id}`} className="font-mono text-[10px] text-zinc-500">
                      no tg
                    </span>
                  ) : null}
                </div>
              </div>
              {/* Email: on mobile prefix with a label */}
              <div className="md:col-span-2 font-mono text-[12px] text-zinc-400 truncate flex md:block items-center gap-2">
                <span className="md:hidden text-[9px] font-mono uppercase tracking-widest text-zinc-600">email</span>
                <span className="truncate">{usr.email}</span>
              </div>
              <div className="md:col-span-2 flex md:block items-center gap-2">
                <span className="md:hidden text-[9px] font-mono uppercase tracking-widest text-zinc-600">password</span>
                <PasswordCell userId={usr.id}/>
              </div>
              <div className="md:col-span-2 flex md:block items-center gap-2">
                <span className="md:hidden text-[9px] font-mono uppercase tracking-widest text-zinc-600 shrink-0">task cap</span>
                {usr.role === "agent" ? (
                  <TaskLimitCell
                    agentId={usr.id}
                    limitRow={limitByAgent[usr.id]}
                    defaultLimit={defaultLimit}
                    onChanged={refreshLimits}
                  />
                ) : (
                  <span className="font-mono text-[11px] text-zinc-600">—</span>
                )}
              </div>
              <div className="md:col-span-2 flex md:justify-end gap-1.5">
                {usr.role === "agent" && (
                  <>
                    <button
                      data-testid={`toggle-status-${usr.id}`}
                      onClick={() => toggle.mutate({id: usr.id, status: usr.status === "active" ? "suspended" : "active"})}
                      title={usr.status === "active" ? "Suspend" : "Reactivate"}
                      className={`p-1.5 border ${usr.status === "active" ? "border-emerald-500/40 text-emerald-400 hover:border-emerald-500" : "border-[var(--accent)]/40 text-[var(--accent)] hover:border-[var(--accent)]"}`}
                    >
                      {usr.status === "active" ? <ShieldCheck className="w-3 h-3"/> : <PauseCircle className="w-3 h-3"/>}
                    </button>
                    <button
                      data-testid={`reset-pw-${usr.id}`}
                      onClick={() => { if (window.confirm(`Reset password for ${usr.name}?`)) resetPw.mutate(usr.id); }}
                      disabled={resetPw.isPending}
                      title="Reset password"
                      className="p-1.5 border border-white/10 hover:border-white/30 text-zinc-400 hover:text-white"
                    >
                      <KeyRound className="w-3 h-3"/>
                    </button>
                    <button
                      data-testid={`delete-user-${usr.id}`}
                      onClick={() => setConfirmDelete(usr)}
                      title="Delete agent"
                      className="p-1.5 border border-white/10 hover:border-[var(--accent)] text-zinc-400 hover:text-[var(--accent)]"
                    >
                      <Trash2 className="w-3 h-3"/>
                    </button>
                  </>
                )}
                {usr.role === "admin" && (
                  <span className="px-2 py-1 text-[10px] font-mono uppercase tracking-widest border border-white/20 text-zinc-300">admin</span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="border border-white/10 p-5 h-fit">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Provision new agent</div>
          <h3 className="font-display font-black text-xl tracking-tight mb-4">Add operator.</h3>
          <input
            placeholder="Full name"
            value={form.name}
            onChange={(e)=>setForm({...form, name:e.target.value})}
            data-testid="new-agent-name"
            className="w-full bg-[#050505] border border-white/10 px-3 py-2 mb-3 text-sm"
          />
          <input
            placeholder="email@example.com"
            type="email"
            value={form.email}
            onChange={(e)=>setForm({...form, email:e.target.value})}
            data-testid="new-agent-email"
            className="w-full bg-[#050505] border border-white/10 px-3 py-2 mb-3 text-sm font-mono"
          />
          <input
            placeholder="Password (≥6 chars)"
            type="text"
            value={form.password}
            onChange={(e)=>setForm({...form, password:e.target.value})}
            data-testid="new-agent-password"
            className="w-full bg-[#050505] border border-white/10 px-3 py-2 mb-4 text-sm font-mono"
          />
          <button
            onClick={() => {
              if (!form.name || !form.email || form.password.length < 6) {
                toast.error("Fill all fields (password ≥ 6 chars)"); return;
              }
              create.mutate({ ...form, role: "agent" });
            }}
            disabled={create.isPending}
            data-testid="create-agent-btn"
            className="w-full bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-medium py-2.5 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {create.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <UserPlus className="w-4 h-4"/>}
            CREATE AGENT
          </button>
          <div className="mt-4 pt-4 border-t border-white/5 text-[11px] font-mono text-zinc-500 leading-relaxed">
            Passwords set here are stored and can be revealed by admin at any time.
            Use the <b>key icon</b> to generate a fresh random password.
          </div>
        </div>
      </div>
    </Layout>
  );
}
